import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { AppConstants } from '../../../utils/AppConstants';
import { BaseSearchCriteria } from '../../../models/searchCriteria/BaseSearchCriteria';
import { TooltipDirective } from '../../../utils/tooltip.directive';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { ThOrderingComponent } from '../../shared/th-ordering/th-ordering.component';
import { FilterPanelComponent } from '../../shared/filter-panel/filter-panel.component';
import { AlertService } from '../../shared/alert/alert.service';
import { StorageService } from '../../../services/storage.service';
import { Guardian } from '../../../models/Guardian';
import { GuardianService } from '../../../services/guardian.service';
import { GUARDIAN_SEARCH_CRITERIA_KEY, GuardianSearchCriteria } from '../../../models/searchCriteria/GuardianSearchCriteria';

@Component({
  selector: 'app-judicial-deposits',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    ThOrderingComponent,
    ConfirmAlertComponent,
    HasPermissionDirective,
    FilterPanelComponent,
    FormsModule,
    ReactiveFormsModule,
    TooltipDirective,
  ],
  templateUrl: './judicial-deposits.component.html',
  styleUrl: './judicial-deposits.component.scss',
})
export class JudicialDepositsComponent implements OnInit {

  searchForm!: FormGroup;
  emptySearchCriteria: GuardianSearchCriteria = { companyName: undefined, pageSize: AppConstants.pageSize, pageNumber: 1 };
  searchCriteria: GuardianSearchCriteria = { ...this.emptySearchCriteria };

  guardians: Guardian[] = [];

  selectedGuardian?: Guardian;
  totalItems: number = 0;

  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';
  confirmOperationType: 'DEL' | undefined = undefined;

  constructor(
    private guardianService: GuardianService,
    private alertService: AlertService,
    private storageService: StorageService,
    private fullSpinnerService: FullSpinnerService,
    private fb: FormBuilder,
    private router: Router) {
    this.searchForm = this.fb.group({
      companyName: [undefined],
    });
    this.clearFilter();
    fullSpinnerService.spinner(undefined);
  }

  ngOnInit(): void {
    this.fullSpinnerService.spinner(undefined);
    if (AppConstants.storeNavigationInfo) {
      let scObj = this.storageService.retrieveNavigationInfo(GUARDIAN_SEARCH_CRITERIA_KEY);
      if (scObj !== null) {
        this.searchCriteria = scObj;
      } else {
        this.storageService.saveNavigationInfo(this.searchCriteria, GUARDIAN_SEARCH_CRITERIA_KEY);
      }
    }
    this.searchForm.patchValue(this.searchCriteria);
    this.find();
  }

  edit(id?: number) {
    this.router.navigate(['guardians', id]);
  }

  find() {
    this.fullSpinnerService.spinner('');
    this.storageService.saveNavigationInfo(this.searchCriteria, GUARDIAN_SEARCH_CRITERIA_KEY);
    this.guardianService.find(this.searchCriteria).subscribe({
      next: (res) => {
        this.guardians = res.items;
        this.totalItems = res.totalItems;
      }, complete: () => {
        this.fullSpinnerService.spinner(undefined);
      }, error: () => {
        this.fullSpinnerService.spinner(undefined);
      }
    });
  }

  search(): void {
    this.searchCriteria = {
      ...this.searchCriteria,
      ...this.searchForm.value
    };
    this.find();
  }

  clearFilter(withSearch: boolean = false) {
    this.searchCriteria = { ...this.emptySearchCriteria };
    this.searchForm.patchValue(this.searchCriteria);
    if (withSearch)
      this.find();
  }

  pageChanged(event: number) {
    this.searchCriteria.pageNumber = event;
    this.find();
  }

  ordering(baseSearchCriteria: BaseSearchCriteria | undefined) {
    this.searchCriteria = baseSearchCriteria as GuardianSearchCriteria;
    this.find();
  }

  confirmOperation(guardian: Guardian, confirmOperationType: 'DEL' | undefined) {
    this.showConfirmationModal = true;
    this.confirmOperationType = confirmOperationType;
    if (confirmOperationType === 'DEL') {
      this.confirmationMessage = 'Eliminazione deposito - Sicuro di voler eliminare ' + guardian.companyName + ' ?';
    }
    this.selectedGuardian = guardian;
  }

  cancelModalOperationExec() {
    this.selectedGuardian = undefined;
    this.showConfirmationModal = false;
  }

  modalOperationExec() {
    this.fullSpinnerService.spinner('');
    if (this.confirmOperationType === 'DEL') {
      this.guardianService.delete(this.selectedGuardian!.id!).subscribe({
        next: () => {
          this.alertService.addErrors(AppConstants.titleDeleteSuccessMessage, [AppConstants.successMessage], true);
          this.cancelModalOperationExec();
          this.fullSpinnerService.spinner(undefined);
          this.find();
        }, error: () => {
          this.cancelModalOperationExec();
          this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
          this.fullSpinnerService.spinner(undefined);
        }
      });
    }
  }
}
